/**
 * Where the signed-in session lives: localStorage for persistence, a React
 * context for the components that need to know who is looking.
 *
 * Two rules shape this file. First, the session token is a bearer token, so it
 * is stored in localStorage rather than a cookie and is attached explicitly to
 * every request. Second, localStorage is not guaranteed to work — Safari private
 * windows and locked-down browser profiles throw on access, not just on write —
 * so every touch is guarded and falls back to memory. Losing persistence costs
 * you a re-login after a reload; it does not break the panel.
 *
 * Expiry is enforced here as well as on the server, the same discipline the API
 * uses for its own time-boxed rows: a lapsed session is treated as absent rather
 * than flashing an authenticated shell that the next request will throw you out
 * of.
 */

import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { otpVerifyResponseSchema } from '@pbtv/shared';
import type { SessionUser } from '@pbtv/shared';
import { setAuthToken, setUnauthorizedHandler } from './api.js';
import { requestOtp, signOutRemote, verifyOtp } from './auth.js';
import type { SignedInSession } from './auth.js';

const STORAGE_KEY = 'pbtv.session.v1';

/** What we persist is exactly what the verify endpoint hands back. */
const storedSessionSchema = otpVerifyResponseSchema;

export type StoredSession = SignedInSession;

// ---------------------------------------------------------------------------
// Storage, guarded
// ---------------------------------------------------------------------------

let cachedStorage: Storage | null | undefined;
let memoryValue: string | null = null;

function usableStorage(): Storage | null {
  if (cachedStorage !== undefined) return cachedStorage;
  try {
    const candidate = window.localStorage;
    const probeKey = `${STORAGE_KEY}.probe`;
    candidate.setItem(probeKey, '1');
    candidate.removeItem(probeKey);
    cachedStorage = candidate;
  } catch {
    cachedStorage = null;
  }
  return cachedStorage;
}

/** False when the browser refused persistent storage and we fell back to memory. */
export function isStoragePersistent(): boolean {
  return usableStorage() !== null;
}

function readRaw(): string | null {
  const storage = usableStorage();
  if (storage === null) return memoryValue;
  try {
    return storage.getItem(STORAGE_KEY);
  } catch {
    return memoryValue;
  }
}

function writeRaw(value: string | null): void {
  memoryValue = value;
  const storage = usableStorage();
  if (storage === null) return;
  try {
    if (value === null) storage.removeItem(STORAGE_KEY);
    else storage.setItem(STORAGE_KEY, value);
  } catch {
    // Quota or a profile that revoked access mid-session; memory still holds it.
  }
}

export function clearStoredSession(): void {
  writeRaw(null);
}

export function writeStoredSession(session: StoredSession): void {
  writeRaw(JSON.stringify(session));
}

/** The stored session, or null if there is none, it is malformed, or it has lapsed. */
export function readStoredSession(nowMs: number = Date.now()): StoredSession | null {
  const raw = readRaw();
  if (raw === null) return null;

  let decoded: unknown;
  try {
    decoded = JSON.parse(raw) as unknown;
  } catch {
    clearStoredSession();
    return null;
  }

  const parsed = storedSessionSchema.safeParse(decoded);
  if (!parsed.success) {
    clearStoredSession();
    return null;
  }

  if (Date.parse(parsed.data.expiresAt) <= nowMs) {
    clearStoredSession();
    return null;
  }

  return parsed.data;
}

// ---------------------------------------------------------------------------
// React context
// ---------------------------------------------------------------------------

/**
 * Why the panel is showing a login form. A session that ended because the server
 * rejected the token reads differently from one the user ended deliberately, and
 * saying which is the difference between "sign in again" and "why am I here".
 */
export type SignOutReason = 'signed-out' | 'session-expired';

export interface SessionContextValue {
  user: SessionUser | null;
  token: string | null;
  isSignedIn: boolean;
  /** False when the session will not survive a reload. Worth telling the user. */
  isPersistent: boolean;
  /** Set when a session ended without the user asking; cleared on the next sign-in. */
  lastSignOutReason: SignOutReason | null;
  requestSignInCode: (email: string, turnstileToken: string) => Promise<void>;
  signIn: (email: string, code: string) => Promise<SessionUser>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }): ReactElement {
  const queryClient = useQueryClient();

  const [session, setSession] = useState<StoredSession | null>(() => {
    const existing = readStoredSession();
    // Set synchronously, before any child mounts: a child effect that fires a
    // request on first render must find the token already in place.
    setAuthToken(existing?.token ?? null);
    return existing;
  });
  const [lastSignOutReason, setLastSignOutReason] = useState<SignOutReason | null>(null);

  const forget = useCallback(
    (reason: SignOutReason) => {
      clearStoredSession();
      setAuthToken(null);
      setSession(null);
      setLastSignOutReason(reason);
      // Cached store and fleet data belongs to the session that fetched it.
      // Leaving it behind would show the next person to sign in on this browser
      // a flash of an estate they may not be allowed to see.
      queryClient.clear();
    },
    [queryClient],
  );

  useEffect(() => {
    setUnauthorizedHandler(() => forget('session-expired'));
    return () => setUnauthorizedHandler(null);
  }, [forget]);

  const value = useMemo<SessionContextValue>(
    () => ({
      user: session?.user ?? null,
      token: session?.token ?? null,
      isSignedIn: session !== null,
      isPersistent: isStoragePersistent(),
      lastSignOutReason,
      requestSignInCode: requestOtp,
      signIn: async (email: string, code: string) => {
        const next = await verifyOtp(email, code);
        queryClient.clear();
        writeStoredSession(next);
        setAuthToken(next.token);
        setSession(next);
        setLastSignOutReason(null);
        return next.user;
      },
      signOut: async () => {
        // A token the server has already dropped must not trap you in the panel.
        await signOutRemote().catch(() => undefined);
        forget('signed-out');
      },
    }),
    [session, lastSignOutReason, forget, queryClient],
  );

  return createElement(SessionContext.Provider, { value }, children);
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (value === null) {
    throw new Error('useSession must be used inside a SessionProvider');
  }
  return value;
}
